import { formatCurrency, formatDecimalUnit, formatDecimalWithGroupedUnit } from "@/common/utils/formatters";
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl";
import { secondsToInterval } from "@/common/utils/secondsToInterval";
import { establishmentBlockModeOptions } from "@/features/privateCompanies/constants/establishmentBlockModeOptions.constant";
import { fiscalDocumentKindOptions } from "@/features/privateCompanies/constants/fiscalDocumentKind.constant";
import { CompanyBranchData } from "@/features/privateCompanies/types/companyBranches.type";
import { companyBranchDefaultPeriodRestrictionData } from "@/features/privateCompanies/utils/companyBranchDefaultPeriodRestrictionData";
import { establishmentOptionTransformer } from "@/features/privateCompanies/utils/establishmentOptionTransformer";
import { ControlledByType } from "../types/companyGovernmentAllocation.type";

export const companyBranchDefaultValues = ({
  branchData,
  controlledBy,
}: {
  branchData?: CompanyBranchData,
  controlledBy?: ControlledByType,
}) => {
  const isQuantity = controlledBy === 'quantity';

  return {
    parent: branchData?.parent_id
      ? { value: branchData?.parent_id, label: branchData?.parent_display_name }
      : undefined,
    display_name: branchData?.display_name,
    should_have_own_bill_when_dynamic: branchData?.should_have_own_bill_when_dynamic || false,
    master_client_user_id: branchData?.master_client_user?.id
      ? {
          label: branchData?.master_client_user.full_name,
          value: branchData?.master_client_user.id,
        }
      : undefined,
    general_information: {
      willUseOwnData: !!branchData?.general_information || !branchData?.parent_id,
      ...branchData?.general_information,
      address_attributes: {
        ...branchData?.general_information?.address,
      },
    },
    enableEstablishmentRestriction: !!branchData?.restriction?.allowed_or_blocked_establishments.length,
    establishment_block_mode:
      branchData?.restriction?.establishment_block_mode
        ? establishmentBlockModeOptions.find((option) => option.value === branchData?.restriction?.establishment_block_mode)
        : undefined,
    allowed_or_blocked_establishments_attributes:
      branchData?.restriction?.allowed_or_blocked_establishments?.length
        ? branchData?.restriction?.allowed_or_blocked_establishments.map(establishmentOptionTransformer)
        : undefined,
    minimum_seconds_interval_between_consumptions: {
      days: secondsToInterval(branchData?.restriction?.minimum_seconds_interval_between_consumptions || 0).days,
      hours: secondsToInterval(branchData?.restriction?.minimum_seconds_interval_between_consumptions || 0).hours,
      minutes: secondsToInterval(branchData?.restriction?.minimum_seconds_interval_between_consumptions || 0).minutes,
      seconds: secondsToInterval(branchData?.restriction?.minimum_seconds_interval_between_consumptions || 0).seconds,
    },
    minimum_kilometers_usage_between_consumptions:
      Number(branchData?.restriction?.minimum_kilometers_usage_between_consumptions) > 0
        ? `${branchData?.restriction?.minimum_kilometers_usage_between_consumptions}`
        : undefined,
    maximum_kilometers_usage_between_consumptions:
      Number(branchData?.restriction?.maximum_kilometers_usage_between_consumptions) > 0
        ? `${branchData?.restriction?.maximum_kilometers_usage_between_consumptions}`
        : undefined,
    minimum_seconds_usage_between_consumptions: {
      days: secondsToInterval(branchData?.restriction?.minimum_seconds_usage_between_consumptions || 0).days,
      hours: secondsToInterval(branchData?.restriction?.minimum_seconds_usage_between_consumptions || 0).hours,
      minutes: secondsToInterval(branchData?.restriction?.minimum_seconds_usage_between_consumptions || 0).minutes,
      seconds: secondsToInterval(branchData?.restriction?.minimum_seconds_usage_between_consumptions || 0).seconds,
    },
    maximum_seconds_usage_between_consumptions: {
      days: secondsToInterval(branchData?.restriction?.maximum_seconds_usage_between_consumptions || 0).days,
      hours: secondsToInterval(branchData?.restriction?.maximum_seconds_usage_between_consumptions || 0).hours,
      minutes: secondsToInterval(branchData?.restriction?.maximum_seconds_usage_between_consumptions || 0).minutes,
      seconds: secondsToInterval(branchData?.restriction?.maximum_seconds_usage_between_consumptions || 0).seconds,
    },
    product_category_restrictions_attributes: branchData?.restriction?.product_category_restrictions?.map((categoryRestriction) => ({
      id: categoryRestriction.id,
      product_category_id: categoryRestriction.product_category.id,
      display_name: categoryRestriction.product_category.display_name,
      minimum_kilometers_usage_between_consumptions:
        Number(categoryRestriction.minimum_kilometers_usage_between_consumptions) > 0
          ? `${categoryRestriction.minimum_kilometers_usage_between_consumptions}`
          : undefined,
      maximum_kilometers_usage_between_consumptions:
        Number(categoryRestriction.maximum_kilometers_usage_between_consumptions) > 0
          ? `${categoryRestriction.maximum_kilometers_usage_between_consumptions}`
          : undefined,
      minimum_seconds_usage_between_consumptions: {
        days: secondsToInterval(categoryRestriction.minimum_seconds_usage_between_consumptions || 0).days,
        hours: secondsToInterval(categoryRestriction.minimum_seconds_usage_between_consumptions || 0).hours,
        minutes: secondsToInterval(categoryRestriction.minimum_seconds_usage_between_consumptions || 0).minutes,
        seconds: secondsToInterval(categoryRestriction.minimum_seconds_usage_between_consumptions || 0).seconds,
      },
      maximum_seconds_usage_between_consumptions: {
        days: secondsToInterval(categoryRestriction.maximum_seconds_usage_between_consumptions || 0).days,
        hours: secondsToInterval(categoryRestriction.maximum_seconds_usage_between_consumptions || 0).hours,
        minutes: secondsToInterval(categoryRestriction.maximum_seconds_usage_between_consumptions || 0).minutes,
        seconds: secondsToInterval(categoryRestriction.maximum_seconds_usage_between_consumptions || 0).seconds,
      },
    })),
    enablePeriodRestriction: !!branchData?.restriction?.period_restrictions.length,
    period_restrictions_attributes: companyBranchDefaultPeriodRestrictionData(branchData?.restriction?.period_restrictions),
    granted_credit: formatCurrency(Number(branchData?.granted_credit)),
    enableCreditConfiguration: !!branchData?.product_category_granted_credits?.length,
    product_category_granted_credits_attributes: branchData?.product_category_granted_credits?.map((category) => ({
      id: category.id,
      granted_credit: isQuantity ? undefined : formatCurrency(Number(category.granted_credit)),
      granted_quantity: isQuantity ? formatDecimalWithGroupedUnit(
        Number(category.granted_quantity),
        category.product_granted_credits,
        (p) => p.product.unit_for_quantity,
      ) : undefined,
      product_category_id: category.product_category.id,
      display_name: category.product_category.display_name,
      product_granted_credits_attributes: category.product_granted_credits?.map((product) => ({
        id: product.id,
        product_id: product.product.id,
        granted_credit: isQuantity ? undefined : formatCurrency(Number(product.granted_credit)),
        granted_quantity: isQuantity ? formatDecimalUnit(Number(product.granted_quantity), product.product.unit_for_quantity) : undefined,
        display_name: product.product.display_name,
        product_unit_for_quantity: product.product.unit_for_quantity,
      })),
    })),
    fiscal_document_information_attributes: {
      fiscal_document_kind: fiscalDocumentKindOptions.find(
        ({ value }) => value === branchData?.fiscal_document_information?.fiscal_document_kind,
      ),
      consumption_observations: branchData?.fiscal_document_information?.consumption_observations,
      service_observations: branchData?.fiscal_document_information?.service_observations,
    },
    name_on_card: branchData?.name_on_card || '',
    additional_documents_attachments_attributes: branchData?.additional_documents.map((attachment) => ({
      id: attachment.id,
      fileName: getFilenameFromUrl(attachment?.url),
      currentFileUrl: attachment?.url
    }))
  }
}
